import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton,
  InputLabel, Menu, MenuItem, Select, Snackbar, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import {
  ArrowRight, BookOpen, CalendarDays, Eye, MoreVertical, Plus, Shuffle, Users, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TemplateBuilderCard from '../components/Homepage/TemplateBuilderCard';
import StudentManagementDrawer from '../components/StudentManagementDrawer';
import { getActivityTemplateLabel } from '../utils/activityTemplateLabel';
import { distributeStudents, moveStudentBetweenGroups } from '../utils/courseGrouping';
import url from '../url';

const colors = {
  page: '#0c1529',
  panel: '#111d35',
  card: '#172540',
  border: '#293d62',
  text: '#e6edff',
  muted: '#b8c7e2',
  purple: '#7958f5',
  field: {
    '& .MuiInputLabel-root': { color: '#b8c7e2' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#b9aaff' },
    '& .MuiInputBase-input, & .MuiSelect-select': { color: '#e6edff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#60769d' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#91a6cc' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#9b86ff' },
    '& .MuiSvgIcon-root': { color: '#b8c7e2' }
  }
};

const authConfig = () => {
  const token = localStorage.getItem('jwtToken');
  return {
    withCredentials: true,
    headers: token ? { Authorization: 'Bearer ' + token } : undefined
  };
};

const dateLabel = (date) => date
  ? new Date(date).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/-/g, '/')
  : '';

const groupStudentIds = (group) => (
  group.studentIds || (group.members || []).map((member) => Number(member.studentUserId))
);

export default function Studio({ userId }) {
  const { t } = useTranslation('homepage');
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [studentManagementOpen, setStudentManagementOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [course, setCourse] = useState(null);
  const [classes, setClasses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [courseDialog, setCourseDialog] = useState(null);
  const [courseForm, setCourseForm] = useState({ name: '', description: '', studentClassId: '', activityTemplateId: '' });
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [editableGroups, setEditableGroups] = useState([]);
  const [groupCount, setGroupCount] = useState(3);
  const [draggedStudentId, setDraggedStudentId] = useState(null);
  const [visibleGroup, setVisibleGroup] = useState(null);
  const [cardMenu, setCardMenu] = useState({ anchor: null, course: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [publishConfirmation, setPublishConfirmation] = useState('');

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setCourse(null);
      return null;
    }
    const response = await axios.get(`${url.backendHost}api/courses/${id}`, authConfig());
    setCourse(response.data);
    return response.data;
  }, []);

  const loadCourses = useCallback(async (preferredId) => {
    const response = await axios.get(`${url.backendHost}api/courses`, authConfig());
    const nextCourses = response.data.courses || [];
    setCourses(nextCourses);
    const nextId = preferredId && nextCourses.some((item) => item.id === preferredId)
      ? preferredId
      : (nextCourses[0]?.id || null);
    setSelectedId(nextId);
    await loadDetail(nextId);
  }, [loadDetail]);

  const loadResources = useCallback(async () => {
    if (!userId) return;
    const [classResponse, templateResponse] = await Promise.all([
      axios.get(`${url.backendHost}api/student-classes`, authConfig()),
      axios.get(`${url.backendHost}api/activity-templates/user/${userId}`, authConfig())
    ]);
    setClasses(classResponse.data.classes || []);
    setTemplates(templateResponse.data || []);
  }, [userId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([loadResources(), loadCourses()])
      .catch(() => { if (active) setError('無法載入課程資料，請稍後再試。'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadCourses, loadResources]);

  const studentList = useMemo(() => (
    classes.find((item) => Number(item.id) === Number(course?.studentClassId))?.members || []
  ), [classes, course?.studentClassId]);

  const openCourseDialog = (mode, selected = null) => {
    setCourseDialog(mode);
    setCourseForm(selected ? {
      name: selected.name || '',
      description: selected.description || '',
      studentClassId: String(selected.studentClassId || ''),
      activityTemplateId: String(selected.activityTemplateId || '')
    } : {
      name: '',
      description: '',
      studentClassId: classes[0] ? String(classes[0].id) : '',
      activityTemplateId: templates[0] ? String(templates[0].id) : ''
    });
  };

  const saveCourse = async () => {
    if (!courseForm.name.trim() || !courseForm.studentClassId || !courseForm.activityTemplateId) return;
    setLoading(true);
    try {
      const payload = {
        ...courseForm,
        name: courseForm.name.trim(),
        studentClassId: Number(courseForm.studentClassId),
        activityTemplateId: Number(courseForm.activityTemplateId)
      };
      if (courseDialog === 'edit' && Number(course?.studentClassId) !== payload.studentClassId
        && course?.groups?.length
        && !window.confirm('切換班級將重置目前的課程小組名單，是否繼續？')) return;
      const response = courseDialog === 'edit'
        ? await axios.put(`${url.backendHost}api/courses/${course.id}`, payload, authConfig())
        : await axios.post(`${url.backendHost}api/courses`, payload, authConfig());
      setCourseDialog(null);
      await loadCourses(response.data.id);
    } catch (requestError) {
      setError(requestError.response?.data?.message || '儲存課程失敗。');
    } finally {
      setLoading(false);
    }
  };

  const openGroupsDialog = () => {
    const groups = (course?.groups || []).map((group) => ({
      id: group.id,
      name: group.name,
      studentIds: groupStudentIds(group)
    }));
    setEditableGroups(groups);
    setGroupCount(Math.max(1, groups.length || Math.min(3, studentList.length || 1)));
    setGroupsDialogOpen(true);
  };

  const autoAssignStudents = (shuffle = false) => {
    const studentIds = studentList.map((student) => Number(student.id));
    setEditableGroups(distributeStudents(studentIds, editableGroups, groupCount, shuffle));
  };

  const dropStudentInGroup = (event, targetIndex) => {
    event.preventDefault();
    const draggedValue = event.dataTransfer.getData('text/plain');
    if (!draggedValue && draggedStudentId === null) return;
    const studentId = Number(draggedValue || draggedStudentId);
    if (!Number.isInteger(studentId)) return;
    setEditableGroups((groups) => moveStudentBetweenGroups(groups, studentId, targetIndex));
    setDraggedStudentId(null);
  };

  const renderDraggableStudent = (student) => {
    const assignedIndex = editableGroups.findIndex((group) => group.studentIds.some((id) => Number(id) === Number(student.id)));
    return (
      <Box
        key={student.id}
        draggable
        onDragStart={(event) => {
          event.dataTransfer.setData('text/plain', String(student.id));
          event.dataTransfer.effectAllowed = 'move';
          setDraggedStudentId(Number(student.id));
        }}
        onDragEnd={() => setDraggedStudentId(null)}
        sx={{
          px: 1.25,
          py: .75,
          border: `1px solid ${colors.border}`,
          borderRadius: 1.5,
          bgcolor: draggedStudentId === Number(student.id) ? '#283b60' : '#111d35',
          color: colors.text,
          '& .MuiTypography-caption': { color: colors.muted },
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' }
        }}
      >
        <Typography variant="body2">{student.name || student.email}</Typography>
        {student.name && <Typography variant="caption" color={colors.muted}>{student.email}</Typography>}
        <Select
          size="small"
          value={assignedIndex < 0 ? '' : String(assignedIndex)}
          displayEmpty
          inputProps={{ 'aria-label': `分配 ${student.name || student.email} 的小組` }}
          onChange={(event) => setEditableGroups((groups) => moveStudentBetweenGroups(
            groups,
            student.id,
            event.target.value === '' ? null : Number(event.target.value)
          ))}
          MenuProps={{
            PaperProps: {
              sx: { bgcolor: colors.panel, color: colors.text, border: `1px solid ${colors.border}` }
            }
          }}
          sx={{
            color: colors.text,
            mt: .5,
            fontSize: 12,
            maxWidth: 145,
            height: 30,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#60769d' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#91a6cc' },
            '& .MuiSvgIcon-root': { color: colors.muted }
          }}
        >
          <MenuItem value="">未分組</MenuItem>
          {editableGroups.map((group, index) => <MenuItem key={group.id || index} value={String(index)}>{group.name}</MenuItem>)}
        </Select>
      </Box>
    );
  };

  const saveGroups = async () => {
    setLoading(true);
    try {
      await axios.put(`${url.backendHost}api/courses/${course.id}/groups`, { groups: editableGroups }, authConfig());
      setGroupsDialogOpen(false);
      await loadCourses(course.id);
    } catch (requestError) {
      setError(requestError.response?.data?.message || '儲存小組設定失敗。');
    } finally {
      setLoading(false);
    }
  };

  const removeCourse = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await axios.delete(`${url.backendHost}api/courses/${deleteTarget.id}`, authConfig());
      setDeleteTarget(null);
      setCardMenu({ anchor: null, course: null });
      await loadCourses(selectedId === deleteTarget.id ? null : selectedId);
    } catch (requestError) {
      setError(requestError.response?.data?.message || '刪除課程失敗。');
    } finally {
      setLoading(false);
    }
  };

  const submitPublish = async (confirmPackageChange = false) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${url.backendHost}api/courses/${course.id}/publish`,
        { confirmPackageChange },
        authConfig()
      );
      try {
        const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, authConfig());
        if (renewResponse.data.jwtToken) {
          localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
        }
      } catch (renewError) {
        console.error('Unable to refresh group membership access.', renewError);
      }
      setPublishConfirmation('');
      await loadCourses(course.id);
      setNotice(response.data.message === 'Course activities updated successfully.'
        ? '課程與小組活動已成功更新！'
        : '課程與小組活動已成功建立！');
    } catch (requestError) {
      if (requestError.response?.status === 409 && requestError.response?.data?.code === 'PACKAGE_CHANGE_CONFIRMATION_REQUIRED') {
        setPublishConfirmation('package');
      } else {
        setError(requestError.response?.data?.message || '送出課程活動失敗。');
      }
    } finally {
      setLoading(false);
    }
  };

  const publish = () => {
    if (course?.hasRemovedSyncedGroups) {
      setPublishConfirmation('groups');
      return;
    }
    submitPublish();
  };

  const canPublish = (course?.groups || []).some((group) => groupStudentIds(group).length > 0);

  if (showTemplateBuilder) {
    const TemplateBuilder = require('./TemplateBuilder/TemplateBuilder').default;
    return <TemplateBuilder onBack={() => setShowTemplateBuilder(false)} userId={userId} />;
  }

  return (
    <Box sx={{ bgcolor: colors.page, minHeight: '100vh', pb: 5, color: colors.text }}>
      <Box sx={{ maxWidth: 1500, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
        <Typography variant="h4" fontWeight={800}>{t('advanced.title')}</Typography>
        <Typography sx={{ color: colors.muted, mt: 0.5, mb: 3 }}>
          管理你的長期活動（課程）、班級與教材資源，打造有深度的學習旅程。
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <Card sx={{ background: 'linear-gradient(110deg, #28296b, #234768)', color: 'white', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700}>建立你的長期活動（課程）</Typography>
              <Typography sx={{ color: '#c6d1ef', mt: 0.5, mb: 2 }}>從選擇班級、教材包開始，設定小組結構與學習流程。</Typography>
              <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => openCourseDialog('create')}
                sx={{ bgcolor: colors.purple }}>建立新課程</Button>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: colors.panel, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Avatar sx={{ bgcolor: '#125b5b', color: '#7be8d6', mb: 1 }}><Users size={21} /></Avatar>
              <Typography variant="h6" fontWeight={700}>{t('studentManagement.cardTitle')}</Typography>
              <Typography sx={{ color: colors.muted, my: 1 }}>{t('studentManagement.cardDescription')}</Typography>
              <Button onClick={() => setStudentManagementOpen(true)} endIcon={<ArrowRight size={17} />}>前往管理</Button>
            </CardContent>
          </Card>
          <Box sx={{ '& .MuiCard-root': { bgcolor: colors.panel, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 3 } }}>
            <TemplateBuilderCard onClick={() => setShowTemplateBuilder(true)} />
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={25} /></Box>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, .9fr) minmax(420px, 1.1fr)' }, gap: 2 }}>
          <Card sx={{ bgcolor: colors.panel, border: `1px solid ${colors.border}`, borderRadius: 3, color: colors.text }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>我的課程</Typography>
                  <Typography variant="body2" color={colors.muted}>查看與管理你建立的長期活動</Typography>
                </Box>
                <Button size="small" startIcon={<Plus size={16} />} onClick={() => openCourseDialog('create')}>建立課程</Button>
              </Box>
              <Divider sx={{ borderColor: colors.border }} />
              <Box sx={{ px: 2, py: 1 }}><Chip label={`全部 (${courses.length})`} size="small" sx={{ bgcolor: '#32266c', color: '#c5b6ff' }} /></Box>
              <Stack spacing={1.2} sx={{ p: 1.5, pt: 0, maxHeight: 610, overflowY: 'auto' }}>
                {courses.map((item) => (
                  <Card key={item.id} onClick={() => { setSelectedId(item.id); loadDetail(item.id).catch(() => setError('無法載入課程詳情。')); }}
                    sx={{ bgcolor: colors.card, color: colors.text, border: `1px solid ${selectedId === item.id ? colors.purple : colors.border}`, cursor: 'pointer', borderRadius: 2 }}>
                    <CardContent sx={{ p: '12px !important', display: 'flex', gap: 1.5 }}>
                      <Avatar variant="rounded" sx={{ width: 75, height: 85, bgcolor: '#273d68' }}><BookOpen /></Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Typography fontWeight={700} noWrap sx={{ flex: 1 }}>{item.name}</Typography>
                          <IconButton size="small" onClick={(event) => { event.stopPropagation(); setCardMenu({ anchor: event.currentTarget, course: item }); }}>
                            <MoreVertical size={18} color="#bdc9e4" />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" color={colors.muted} noWrap>{getActivityTemplateLabel(item.activityTemplate)} · {item.studentClass?.name || '班級未設定'}</Typography>
                        <Typography variant="body2" color={colors.muted} sx={{ mt: 1 }}>班級：{item.studentClass?.name} · {item.studentCount || 0} 人</Typography>
                        <Typography variant="caption" color={colors.muted} sx={{ display: 'flex', alignItems: 'center', gap: .5, mt: 1 }}>
                          <CalendarDays size={14} /> {dateLabel(item.updatedAt)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {!courses.length && !loading && (
                  <Box sx={{ textAlign: 'center', py: 5, color: colors.muted }}>
                    <Typography>尚未建立課程</Typography>
                    <Button sx={{ mt: 1 }} onClick={() => openCourseDialog('create')}>建立第一個課程</Button>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: colors.panel, border: `1px solid ${colors.border}`, borderRadius: 3, color: colors.text, minHeight: 430 }}>
            {course ? (
              <>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight={700}>課程詳情</Typography>
                  <Button onClick={() => openCourseDialog('edit', course)}>編輯課程</Button>
                </Box>
                <Divider sx={{ borderColor: colors.border }} />
                <Box sx={{ p: 2 }}>
                  <Card sx={{ bgcolor: colors.card, color: colors.text, border: `1px solid ${colors.border}`, mb: 2 }}>
                    <CardContent sx={{ display: 'flex', gap: 2 }}>
                      <Avatar variant="rounded" sx={{ width: 92, height: 110, bgcolor: '#273d68' }}><BookOpen size={28} /></Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>{course.name}</Typography>
                        <Typography variant="body2" color={colors.muted} sx={{ mt: 1 }}>所屬班級：{course.studentClass?.name}</Typography>
                        <Typography variant="body2" color={colors.muted}>教材包：{getActivityTemplateLabel(course.activityTemplate)}</Typography>
                        <Typography variant="body2" color={colors.muted}>最後更新：{dateLabel(course.updatedAt)}</Typography>
                        {course.description && <Typography variant="body2" sx={{ mt: 1 }}>{course.description}</Typography>}
                      </Box>
                    </CardContent>
                  </Card>
                  <Card sx={{ bgcolor: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" fontWeight={700}>課程小組</Typography>
                        <Button size="small" onClick={openGroupsDialog}>編輯小組</Button>
                      </Box>
                      <Box sx={{ p: 1.5, mb: 1.5, border: `1px solid ${colors.border}`, borderRadius: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Users size={20} color="#92a7d1" />
                        <Typography>{course.studentClass?.name} · {studentList.length} 位學生</Typography>
                      </Box>
                      <Stack spacing={.75}>
                        {(course.groups || []).map((group, index) => (
                          <Box key={group.id} sx={{ p: 1, border: `1px solid ${colors.border}`, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 30, height: 30, bgcolor: ['#2874ce', '#ca8c38', '#2dabae'][index % 3] }}><Users size={16} /></Avatar>
                            <Typography sx={{ flex: 1 }}>{group.name}</Typography>
                            <Typography variant="body2" color={colors.muted}>{groupStudentIds(group).length} 人</Typography>
                            <IconButton size="small" aria-label={`檢視${group.name}成員`} onClick={() => setVisibleGroup(group)}><Eye size={17} /></IconButton>
                          </Box>
                        ))}
                        {!course.groups?.length && <Typography color={colors.muted} sx={{ py: 1 }}>尚未建立小組</Typography>}
                      </Stack>
                    </CardContent>
                  </Card>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {course.isDirty && <Chip size="small" color="warning" label="尚有未同步至活動的設定變更" />}
                    <Tooltip title={!canPublish ? '請先完成課程小組設定與學生分配後再送出' : ''}>
                      <span><Button variant="contained" disabled={!canPublish || loading} onClick={publish} sx={{ bgcolor: colors.purple }}>
                        {course.isSynced ? '送出並更新活動' : '送出並建立活動'}
                      </Button></span>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ minHeight: 430, display: 'grid', placeItems: 'center', color: colors.muted, p: 3, textAlign: 'center' }}>
                <Box><Typography variant="h6">選取課程以檢視詳情</Typography><Typography sx={{ mt: 1 }}>建立課程後即可設定小組並送出活動。</Typography></Box>
              </Box>
            )}
          </Card>
        </Box>
      </Box>

      <StudentManagementDrawer open={studentManagementOpen} onClose={() => { setStudentManagementOpen(false); loadResources().catch(() => {}); }} />
      <Menu anchorEl={cardMenu.anchor} open={Boolean(cardMenu.anchor)} onClose={() => setCardMenu({ anchor: null, course: null })}>
        <MenuItem onClick={() => { openCourseDialog('edit', cardMenu.course); setCardMenu({ anchor: null, course: null }); }}>編輯課程</MenuItem>
        <MenuItem onClick={() => setDeleteTarget(cardMenu.course)}>刪除課程</MenuItem>
      </Menu>

      <Dialog open={Boolean(courseDialog)} onClose={() => setCourseDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{courseDialog === 'edit' ? '編輯課程基本資訊' : '建立新課程'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          <TextField label="課程名稱" required value={courseForm.name} onChange={(event) => setCourseForm({ ...courseForm, name: event.target.value })} />
          <FormControl required>
            <InputLabel>選擇班級</InputLabel>
            <Select label="選擇班級" value={courseForm.studentClassId} onChange={(event) => setCourseForm({ ...courseForm, studentClassId: event.target.value })}>
              {classes.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}（{item.members?.length || 0} 人）</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl required>
            <InputLabel>選擇教材包</InputLabel>
            <Select label="選擇教材包" value={courseForm.activityTemplateId} onChange={(event) => setCourseForm({ ...courseForm, activityTemplateId: event.target.value })}>
              {templates.map((item) => <MenuItem key={item.id} value={String(item.id)}>{getActivityTemplateLabel(item)}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="課程描述" multiline minRows={3} value={courseForm.description} onChange={(event) => setCourseForm({ ...courseForm, description: event.target.value })} />
          {(!classes.length || !templates.length) && <Alert severity="info">請先建立班級與教材包，才能建立課程。</Alert>}
        </DialogContent>
        <DialogActions><Button onClick={() => setCourseDialog(null)}>取消</Button><Button onClick={saveCourse} disabled={!courseForm.name.trim() || !classes.length || !templates.length}>儲存</Button></DialogActions>
      </Dialog>

      <Dialog
        open={groupsDialogOpen}
        onClose={() => setGroupsDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { bgcolor: colors.panel, color: colors.text, border: `1px solid ${colors.border}`, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: colors.text }}>編輯課程小組</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', color: colors.text }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="number"
                label="小組數量"
                value={groupCount}
                onChange={(event) => setGroupCount(Math.max(1, Math.min(100, Number(event.target.value) || 1)))}
                inputProps={{ min: 1, max: 100 }}
                sx={{ ...colors.field, width: 125 }}
              />
              <Button variant="outlined" sx={{ color: '#c8d5ef', borderColor: '#60769d', '&:hover': { borderColor: '#a5b7dc', bgcolor: '#1b2b48' } }} onClick={() => autoAssignStudents(false)} disabled={!studentList.length}>
                平均分組
              </Button>
              <Button variant="outlined" startIcon={<Shuffle size={16} />} sx={{ color: '#c8d5ef', borderColor: '#60769d', '&:hover': { borderColor: '#a5b7dc', bgcolor: '#1b2b48' } }} onClick={() => autoAssignStudents(true)} disabled={!studentList.length}>
                隨機分組
              </Button>
              <Typography variant="caption" sx={{ color: colors.muted }}>拖曳學生卡片即可調整分組</Typography>
            </Box>
            {editableGroups.map((group, index) => {
              const assignedIds = new Set(group.studentIds.map(Number));
              const members = studentList.filter((student) => assignedIds.has(Number(student.id)));
              return (
              <Card
                key={group.id || `new-${index}`}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => dropStudentInGroup(event, index)}
                sx={{ p: 2, border: '1px solid #52698f', bgcolor: colors.card, color: colors.text }}
              >
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField fullWidth label="小組名稱" value={group.name} sx={colors.field} onChange={(event) => setEditableGroups(editableGroups.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} />
                  <IconButton aria-label="刪除小組" sx={{ color: '#ff9b9b', '&:hover': { bgcolor: 'rgba(255, 107, 107, 0.12)' } }} onClick={() => setEditableGroups(editableGroups.filter((_, itemIndex) => itemIndex !== index))}><X /></IconButton>
                </Box>
                <Typography variant="body2" sx={{ color: colors.muted, mb: 1 }}>
                  {members.length} 位學生 · 拖曳學生至此小組
                </Typography>
                <Box sx={{ minHeight: 56, display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 1 }}>
                  {members.map(renderDraggableStudent)}
                </Box>
              </Card>
            );})}
            <Button startIcon={<Plus size={17} />} sx={{ alignSelf: 'flex-start', color: '#c8baff', '&:hover': { bgcolor: 'rgba(121, 88, 245, 0.14)' } }} onClick={() => setEditableGroups([...editableGroups, { name: `第${editableGroups.length + 1}組`, studentIds: [] }])}>新增小組</Button>
            <Box
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => dropStudentInGroup(event, null)}
              sx={{ p: 2, border: '1px dashed #60769d', borderRadius: 2, bgcolor: '#0d182d', color: colors.text }}
            >
              <Typography fontWeight={600}>未分組學生（{studentList.filter((student) => !editableGroups.some((group) => group.studentIds.some((id) => Number(id) === Number(student.id)))).length}）</Typography>
              <Typography variant="caption" sx={{ color: colors.muted }}>可從小組拖曳學生到這裡取消分組</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {studentList
                  .filter((student) => !editableGroups.some((group) => group.studentIds.some((id) => Number(id) === Number(student.id))))
                  .map(renderDraggableStudent)}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button sx={{ color: '#c8d5ef' }} onClick={() => setGroupsDialogOpen(false)}>取消</Button>
          <Button variant="contained" sx={{ bgcolor: colors.purple, color: '#fff', '&:hover': { bgcolor: '#6544df' } }} onClick={saveGroups} disabled={editableGroups.some((group) => !group.name.trim())}>儲存小組</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(visibleGroup)} onClose={() => setVisibleGroup(null)} fullWidth maxWidth="xs">
        <DialogTitle>{visibleGroup?.name} 成員</DialogTitle>
        <DialogContent>
          {(visibleGroup?.members || []).map((member) => (
            <Typography key={member.studentUserId} sx={{ py: .5 }}>{member.student?.name || member.student?.email}</Typography>
          ))}
          {!visibleGroup?.members?.length && <Typography color="text.secondary">此小組目前沒有成員。</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setVisibleGroup(null)}>關閉</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>刪除課程</DialogTitle>
        <DialogContent>確定要刪除「{deleteTarget?.name}」嗎？刪除後課程內的小組設定與學習紀錄將無法復原。原有班級學生名單與教材包不會被刪除。</DialogContent>
        <DialogActions><Button onClick={() => setDeleteTarget(null)}>取消</Button><Button color="error" onClick={removeCourse}>刪除課程</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(publishConfirmation)} onClose={() => setPublishConfirmation('')}>
        <DialogTitle>確認更新課程活動</DialogTitle>
        <DialogContent>
          {publishConfirmation === 'groups'
            ? '偵測到部分課程小組已被移除，更新活動將會封存該小組的活動空間，是否確認送出？'
            : '更換教材包並送出將會重新生成課程與小組活動，是否繼續？'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishConfirmation('')}>取消</Button>
          <Button variant="contained" onClick={() => submitPublish(publishConfirmation === 'package')}>確認送出</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(notice)} autoHideDuration={4000} onClose={() => setNotice('')} message={notice} />
    </Box>
  );
}
